import { LucideIcon } from '@skin-seoul/design-system';
import { AnimatePresence, motion } from 'motion/react';
import { ReviewWriteForm } from './ReviewWriteForm';
import { CompleteView } from './common/CompleteView';
import BottomSheet from './common/BottomSheet';
import { useReviewForm } from '@skin-seoul/service/src/hooks/review/useReviewForm';
import { useReviewModalState } from '@skin-seoul/service/src/hooks/review/useReviewModalState';
import { useReviewSubmission } from '@skin-seoul/service/src/hooks/review/useReviewSubmission';
import type { ReviewModalProps } from '@skin-seoul/service';

interface DragInfo {
  offset: {
    x: number;
    y: number;
  };
  velocity: {
    x: number;
    y: number;
  };
}

const Overlay = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <motion.div
    className="fixed top-0 left-0 right-0 bottom-0 bg-black/70 z-[999998] flex items-center justify-center"
    {...props}
  >
    {children}
  </motion.div>
);

const MobileDrawer = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <motion.div
    className="fixed left-0 right-0 bottom-0 h-[100dvh] bg-white rounded-t-[20px] z-[999999] overflow-hidden flex flex-col"
    style={{ touchAction: 'none' }}
    {...props}
  >
    {children}
  </motion.div>
);

export const ReviewWriteMobileModal = ({
  isOpen,
  productInfo,
  productId,
  onClose,
  onSubmit,
  mode = 'create',
  initialValues,
  existingMedia,
}: ReviewModalProps) => {
  const { formState, formActions, formOptions, isSubmitDisabled, validationErrors, isMember, resetForm } =
    useReviewForm({
      isOpen,
      mode,
      initialValues,
      existingMedia,
    });

  // Also the owner of the page's scroll lock: it holds `body.overflow` for as long as `isOpen`.
  const { isComplete, setIsComplete, handleClose } = useReviewModalState({
    isOpen,
    onClose: () => {
      resetForm();
      onClose();
    },
    hasUnsavedChanges: false,
    enableConfirmation: false,
  });

  const { handleSubmit, submissionError, isSubmitting } = useReviewSubmission({
    formData: formState,
    onSubmit,
    onSubmissionComplete: () => setIsComplete(true),
  });

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: DragInfo) => {
    if (info.offset.y > 200 || info.velocity.y > 200) {
      handleClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && !isComplete && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={handleOverlayClick}
          />
          <MobileDrawer
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            onDragEnd={handleDragEnd}
          >
            <div className="flex flex-row w-full justify-between items-center mb-4 pt-6 px-4">
              <p className="text-paragraph-large font-semibold text-neutral-800">
                {mode === 'edit' ? 'Edit your review' : 'Review this product'}
              </p>
              <button
                onClick={handleClose}
                aria-label="Close modal"
                className="inline-flex items-center justify-center rounded-md h-9 w-9 hover:bg-neutral-100"
              >
                <LucideIcon name="X" width="24px" height="24px" className="text-generic-100" />
              </button>
            </div>

            <div
              className="flex-1 overflow-y-auto pb-6 px-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <ReviewWriteForm
                formData={formState}
                formActions={formActions}
                options={formOptions}
                productInfo={productInfo}
                productId={productId}
                isSubmitDisabled={isSubmitDisabled}
                validationErrors={validationErrors}
                isMember={isMember}
                onSubmit={handleSubmit}
                variant="mobile"
                submissionAlertMessage={submissionError?.message ?? null}
                submissionFlaggedText={submissionError?.flaggedText}
                mode={mode}
                isSubmitting={isSubmitting}
              />
            </div>
          </MobileDrawer>
        </>
      )}

      {isComplete && (
        <BottomSheet isOpen={isComplete} onClose={onClose} title="">
          <CompleteView onContinue={onClose} />
        </BottomSheet>
      )}
    </AnimatePresence>
  );
};
